import axios from 'axios';
import React, { Fragment, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaSpinner, FaImage, FaEdit, FaSave } from "react-icons/fa";
import AdminPageLoader from './AdminPageLoader';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3500';

function EditCategoryPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [categoryData, setCategoryData] = useState({ name: '', image: '', parentCategory: '' });
    const [parentCategories, setParentCategories] = useState([]);
    const [imageUploading, setImageUploading] = useState(false);
    const [imageLoadError, setImageLoadError] = useState(false);
    const [savingCategory, setSavingCategory] = useState(false);
    const [loading, setLoading] = useState(true);
    const token = localStorage.getItem('authToken');

    const handleApiError = (error) => {
        console.error('API Error:', error);
        alert(error.response?.data?.message || 'Something went wrong. Please try again.');
    };

    const getSingleCategory = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/category-data/${id}`);
            setCategoryData(response.data);
            setImageLoadError(false);
        } catch (error) {
            handleApiError(error);
            setTimeout(() => navigate('/admin/categories'), 2000);
        } finally {
            setLoading(false);
        }
    };

    const fetchParentCategories = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/parent-categories`);
            setParentCategories(response.data || []);
        } catch (error) {
            console.error("Failed to fetch parent categories:", error);
        }
    };

    const handleChange = (event) => {
        setCategoryData({
            ...categoryData,
            [event.target.name]: event.target.value,
        });
    };

    const handleImageUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/avif'];
        if (!allowedTypes.includes(file.type)) {
            alert('Only JPG, PNG, WebP, or AVIF images are allowed.');
            return;
        }

        if (file.size > 5 * 1024 * 1024) { // 5MB limit
            alert('File size must be under 5MB.');
            return;
        }

        try {
            setImageUploading(true);
            const formData = new FormData();
            formData.append('image', file);

            const response = await axios.post(`${API_BASE_URL}/upload-prod-img`, formData);
            setCategoryData(prev => ({ ...prev, image: response.data.path }));
            setImageLoadError(false);
            alert('Image uploaded successfully!');
        } catch (error) {
            handleApiError(error);
        } finally {
            setImageUploading(false);
        }
    };

    const validateFields = () => {
        if (!categoryData.name.trim()) {
            alert('Category name is required');
            return false;
        }
        if (!categoryData.image.trim()) {
            alert('Category image is required');
            return false;
        }
        return true;
    };

    const handleUpdateCategory = async () => {
        if (!validateFields()) return;

        try {
            setSavingCategory(true);
            const response = await axios.patch(`${API_BASE_URL}/update-category/${id}`, categoryData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            alert(response.data.message);
            navigate('/admin/categories');
        } catch (error) {
            handleApiError(error);
        } finally {
            setSavingCategory(false);
        }
    };

    const handleBackButtonClick = () => {
        navigate('/admin/categories');
    };

    useEffect(() => {
        getSingleCategory();
        fetchParentCategories();
    }, [id]);

    if (loading) {
        return (
            <div className="container-fluid p-0 g-0 adma">
                <div className="row p-3 pb-2 g-0" style={{ background: '#ffffff', borderBottom: '1px solid #e2e8f0' }}>
                    <div className="col-12">
                        <div className="section-title">
                            <h4 className="mt-1" style={{ fontSize: '16px', fontWeight: '700' }}>EDIT CATEGORY</h4>
                            <p className="text-muted p-0 m-0" style={{ fontSize: '12px' }}>Update category information and image</p>
                        </div>
                    </div>
                </div>
                <div className="p-3">
                    <AdminPageLoader message="Loading category details..." />
                </div>
            </div>
        );
    }

    return (
        <Fragment>
            <div className="container-fluid p-0 g-0 adma">
                <div className="row p-3 pb-2 g-0" style={{ background: '#ffffff', borderBottom: '1px solid #e2e8f0' }}>
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center">
                            <div className="section-title">
                                <h4 className="mt-1" style={{ fontSize: '16px', fontWeight: '700' }}>EDIT CATEGORY</h4>
                                <p className="text-muted p-0 m-0" style={{ fontSize: '12px' }}>Update category information and image</p>
                            </div>
                            <button 
                                className="btn theme-btn-blue-flat btn-sm rounded-0"
                                onClick={handleBackButtonClick}
                                title="Back to Categories"
                                style={{
                                    padding: '6px 16px',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                }}
                            >
                                <FaArrowLeft size={11} />
                                Back to Categories
                            </button>
                        </div>
                    </div>
                </div>

                <div className="row g-0 details-content-row">
                    <div className="col-12">
                        <div className="card rounded-0" style={{ border: 'none', borderBottom: '1px solid #e2e8f0', boxShadow: 'none' }}>
                            <div className="card-header rounded-0" style={{ backgroundColor: '#e8eaf6', borderBottom: '1px solid #d1d5db', padding: '12px 24px', margin: 0, display: 'flex', alignItems: 'center' }}>
                                <h5 className="mb-0" style={{ color: '#1a237e', fontWeight: '700', fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.8px', display: 'flex', alignItems: 'center', margin: 0 }}>
                                    <FaEdit className="me-2" style={{ fontSize: '14px' }} />
                                    Category Information
                                </h5>
                            </div>
                            <div className="card-body p-0">
                                <div className="row g-0">
                                    {/* Left Column: Form Inputs */}
                                    <div className="col-md-6 p-4">
                                        <div className="mb-3">
                                            <label className="form-label fw-bold" style={{ fontSize: '12px', color: '#374151' }}>Category Name</label>
                                            <input
                                                type="text"
                                                className="form-control rounded-0"
                                                style={{ border: '1px solid #cbd5e1', height: '38px', fontSize: '13px' }}
                                                name="name"
                                                value={categoryData.name || ''}
                                                onChange={handleChange}
                                                placeholder="Enter category name"
                                            />
                                        </div>

                                        <div className="mb-3">
                                            <label className="form-label fw-bold" style={{ fontSize: '12px', color: '#374151' }}>Parent Category</label>
                                            <select 
                                                className="form-select rounded-0" 
                                                style={{ border: '1px solid #cbd5e1', height: '38px', fontSize: '13px' }}
                                                name="parentCategory" 
                                                value={categoryData.parentCategory || ''} 
                                                onChange={handleChange}
                                            >
                                                <option value="">Select Parent Category</option>
                                                {parentCategories.map((parent) => (
                                                    <option key={parent._id} value={parent.name}>
                                                        {parent.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        
                                        <div className="mb-4">
                                            <label className="form-label fw-bold" style={{ fontSize: '12px', color: '#374151' }}>Category Image</label>
                                            <input
                                                type="file"
                                                className="form-control rounded-0"
                                                style={{ border: '1px solid #cbd5e1', fontSize: '13px', padding: '7px 12px' }}
                                                name="image"
                                                onChange={handleImageUpload}
                                                accept="image/*"
                                            />
                                            {imageUploading && (
                                                <div className="mt-2 d-flex align-items-center gap-1">
                                                    <FaSpinner className="spinner-icon" style={{ fontSize: '11px', color: '#94a3b8' }} />
                                                    <small style={{ color: '#94a3b8', fontSize: '11px' }}>Uploading image...</small>
                                                </div>
                                            )}
                                            {categoryData.image && !imageUploading && (
                                                <div className="mt-2 d-flex align-items-center gap-1">
                                                    <FaImage style={{ fontSize: '11px', color: '#16a34a' }} />
                                                    <small style={{ color: '#16a34a', fontSize: '11px' }}>Image uploaded successfully</small>
                                                </div>
                                            )}
                                        </div>

                                        <button
                                            className="btn theme-btn-blue rounded-0 w-100"
                                            style={{
                                                height: '38px',
                                                fontSize: '13px',
                                                fontWeight: '600',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '6px'
                                            }}
                                            disabled={savingCategory || imageUploading}
                                            onClick={handleUpdateCategory}
                                        >
                                            {savingCategory ? (
                                                <><FaSpinner className="spinner-icon" style={{ fontSize: '11px' }} /> Updating Category...</>
                                            ) : (
                                                <><FaSave size={13} /> Update Category</>
                                            )}
                                        </button>
                                    </div>
                                    
                                    {/* Right Column: Image Preview */}
                                    <div className="col-md-6 p-4 border-start" style={{ borderColor: '#e2e8f0' }}>
                                        <div className="text-center h-100 d-flex flex-column">
                                            <label className="form-label fw-bold d-block text-start" style={{ fontSize: '12px', color: '#374151', marginBottom: '8px' }}>Current Image</label>
                                            <div className="brand-preview-container flex-grow-1">
                                                {categoryData.image && !imageLoadError ? (
                                                    <div className="image-wrapper w-100 h-100 d-flex align-items-center justify-content-center">
                                                        <img
                                                            src={`${API_BASE_URL}/${categoryData.image}`}
                                                            alt="Category Preview"
                                                            className="brand-preview-image"
                                                            onError={() => setImageLoadError(true)}
                                                        />
                                                    </div>
                                                ) : (
                                                    <div className="brand-preview-placeholder">
                                                        <p className="text-muted mb-0" style={{ fontSize: '13px' }}>No image uploaded</p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <style jsx>{`
                .brand-preview-container {
                    border: 1px solid #cbd5e1;
                    border-radius: 0;
                    padding: 20px;
                    min-height: 220px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #f8fafc;
                    position: relative;
                }
                
                .image-wrapper {
                    position: relative;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 100%;
                    height: 100%;
                }
                
                .brand-preview-image {
                    max-width: 100%;
                    max-height: 200px;
                    object-fit: contain;
                    border-radius: 0;
                    border: 1px solid #e2e8f0;
                }
                
                .spinner-icon {
                    animation: spin 1s linear infinite;
                }
                
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                
                .brand-preview-placeholder {
                    text-align: center;
                    color: #64748b;
                }
                
                .card {
                    border-radius: 0;
                }
                
                .form-control:focus, .form-select:focus {
                    border-color: #1a237e;
                    box-shadow: none;
                }
            `}</style>
        </Fragment>
    );
}

export default EditCategoryPage;
